import CloseIcon from '@mui/icons-material/Close'
import PlayArrowIcon from '@mui/icons-material/PlayArrow'
import PlayCircleFilledIcon from '@mui/icons-material/PlayCircleFilled'
import { Box, Dialog, IconButton } from '@mui/material'
import React, { useCallback, useRef, useState } from 'react'
import { Navigation, Pagination, Zoom, Thumbs, Keyboard } from 'swiper/modules'
import { Swiper, SwiperSlide } from 'swiper/react'

/**
 * FullscreenMediaViewer - A fullscreen media viewer component with Swiper carousel
 *
 * @param {Object} props
 * @param {boolean} props.open - Whether the dialog is open
 * @param {Function} props.onClose - Callback when dialog is closed
 * @param {Array<Object>} props.media - Array of media items:
 *   - For images: { type: 'image', url: string }
 *   - For videos: { type: 'video', url: string, coverUrl: string }
 * @param {number} props.initialIndex - Initial media index to display
 */
const FullscreenMediaViewer = ({ open, onClose, media, initialIndex = 0 }) => {
  const [currentMediaIndex, setCurrentMediaIndex] = useState(initialIndex)
  const [thumbsSwiper, setThumbsSwiper] = useState(null)
  const [playingVideo, setPlayingVideo] = useState(false)
  const videoRef = useRef(null)

  const currentMedia = media[currentMediaIndex]

  const handleClose = useCallback(() => {
    setThumbsSwiper(null)
    setPlayingVideo(false)
    if (videoRef.current) {
      videoRef.current.pause()
      videoRef.current.currentTime = 0
    }
    onClose()
  }, [onClose])

  const handleVideoClick = useCallback(
    (e) => {
      const currentMediaItem = media[currentMediaIndex]
      if (currentMediaItem?.type === 'video') {
        if (playingVideo && videoRef.current) {
          // If video is playing, pause it
          videoRef.current.pause()
          setPlayingVideo(false)
          e.stopPropagation()
        } else {
          // Start playing video
          setPlayingVideo(true)
          if (videoRef.current) {
            videoRef.current.play()
          }
          e.stopPropagation()
        }
      }
    },
    [currentMediaIndex, media, playingVideo],
  )

  const handleVideoPlayButtonClick = useCallback((e) => {
    setPlayingVideo(true)
    if (videoRef.current) {
      videoRef.current.play()
    }
    e.stopPropagation()
  }, [])

  const handleBackgroundClick = (e) => {
    // Close when clicking on Swiper background (not on media or controls)
    const target = e.target
    const isClickOnMedia = target.tagName === 'IMG' || target.tagName === 'VIDEO' || target.closest('img, video')
    const isClickOnControls = target.closest('.swiper-button-next, .swiper-button-prev, .swiper-pagination')
    const isClickOnThumbs = target.closest('.thumbs-swiper')

    // If clicking on background (not media, controls, or thumbs), close dialog
    if (!isClickOnMedia && !isClickOnControls && !isClickOnThumbs) {
      e.stopPropagation()
      handleClose()
    }
  }

  const handleSlideChange = (swiper) => {
    setCurrentMediaIndex(swiper.realIndex)
    setPlayingVideo(false)
    if (videoRef.current) {
      videoRef.current.pause()
      videoRef.current.currentTime = 0
    }
  }

  // Update currentMediaIndex when initialIndex changes
  React.useEffect(() => {
    setCurrentMediaIndex(initialIndex)
  }, [initialIndex])

  return (
    <>
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth={false}
        fullScreen
        sx={{
          '& .MuiDialog-paper': {
            backgroundColor: 'rgba(0, 0, 0, 0.95)',
          },
        }}
      >
        <Box
          sx={{
            width: '100%',
            height: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            position: 'relative',
          }}
        >
          {/* Close Button */}
          <IconButton
            onClick={handleClose}
            sx={{
              position: 'absolute',
              top: 16,
              right: 16,
              color: 'white',
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
              '&:hover': {
                backgroundColor: 'rgba(0, 0, 0, 0.7)',
              },
              zIndex: 2,
            }}
          >
            <CloseIcon />
          </IconButton>

          {/* Fullscreen Swiper with Thumbs */}
          <Box
            sx={{
              width: '100%',
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
            }}
            onClick={handleBackgroundClick}
          >
            {/* Main Swiper */}
            <Box
              sx={{
                width: '100%',
                flex: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: { xs: '60px 10px 10px', sm: '60px 20px 20px' },
                boxSizing: 'border-box',
                overflow: 'hidden',
              }}
            >
              <Swiper
                modules={[Navigation, Pagination, Zoom, Thumbs, Keyboard]}
                navigation={true}
                pagination={{
                  clickable: true,
                  type: 'fraction',
                }}
                zoom={currentMedia?.type === 'image'}
                keyboard={true}
                thumbs={{ swiper: thumbsSwiper && !thumbsSwiper.destroyed ? thumbsSwiper : null }}
                initialSlide={currentMediaIndex}
                loop={false}
                onSlideChange={handleSlideChange}
                style={{
                  width: '100%',
                  height: '100%',
                }}
                className="fullscreen-swiper"
              >
                {media.map((item, index) => (
                  <SwiperSlide key={index}>
                    <Box
                      sx={{
                        width: '100%',
                        height: '100%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        position: 'relative',
                        padding: '10px',
                        boxSizing: 'border-box',
                      }}
                      className={item.type === 'image' ? 'swiper-zoom-container' : ''}
                    >
                      {item.type === 'image' ? (
                        <Box
                          component="img"
                          src={item.url}
                          alt={`Gallery image ${index + 1}`}
                          sx={{
                            width: 'auto',
                            height: 'auto',
                            maxWidth: '100%',
                            maxHeight: '100%',
                            objectFit: 'contain',
                            cursor: 'pointer',
                            display: 'block',
                          }}
                        />
                      ) : (
                        <>
                          {/* Video Poster (shown when not playing) */}
                          {!playingVideo && index === currentMediaIndex && (
                            <Box
                              sx={{
                                position: 'relative',
                                width: '100%',
                                height: '100%',
                                maxWidth: '100%',
                                maxHeight: '100%',
                                objectFit: 'contain',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                              }}
                            >
                              <Box
                                component="img"
                                src={item.coverUrl}
                                alt={`Video poster ${index + 1}`}
                                sx={{
                                  width: 'auto',
                                  height: 'auto',
                                  maxWidth: '100%',
                                  maxHeight: '100%',
                                }}
                              />
                              <Box
                                onClick={handleVideoPlayButtonClick}
                                sx={{
                                  position: 'absolute',
                                  top: '50%',
                                  left: '50%',
                                  transform: 'translate(-50%, -50%)',
                                  color: 'white',
                                  backgroundColor: 'rgba(0, 0, 0, 0.6)',
                                  borderRadius: '50%',
                                  width: 80,
                                  height: 80,
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  cursor: 'pointer',
                                  transition: 'all 0.3s',
                                  '&:hover': {
                                    backgroundColor: 'rgba(0, 0, 0, 0.8)',
                                    transform: 'translate(-50%, -50%) scale(1.1)',
                                  },
                                }}
                              >
                                <PlayCircleFilledIcon sx={{ fontSize: 60 }} />
                              </Box>
                            </Box>
                          )}

                          {/* Video Player (shown when playing) */}
                          {playingVideo && index === currentMediaIndex && (
                            <Box
                              component="video"
                              ref={videoRef}
                              src={item.url}
                              controls
                              autoPlay
                              onClick={(e) => e.stopPropagation()}
                              onEnded={() => setPlayingVideo(false)}
                              sx={{
                                width: 'auto',
                                height: 'auto',
                                maxWidth: '100%',
                                maxHeight: '100%',
                                objectFit: 'contain',
                                display: 'block',
                              }}
                            />
                          )}
                        </>
                      )}
                    </Box>
                  </SwiperSlide>
                ))}
              </Swiper>
            </Box>

            {/* Thumbs Swiper */}
            {media.length > 1 && (
              <Box
                sx={{
                  width: '100%',
                  padding: '10px 60px',
                  backgroundColor: 'rgba(0, 0, 0, 0.3)',
                }}
                className="thumbs-swiper"
              >
                <Swiper
                  modules={[Thumbs]}
                  onSwiper={setThumbsSwiper}
                  spaceBetween={10}
                  slidesPerView="auto"
                  freeMode={true}
                  watchSlidesProgress={true}
                  initialSlide={currentMediaIndex}
                  style={{
                    height: '80px',
                  }}
                  className="thumbs-swiper-container"
                >
                  {media.map((item, index) => (
                    <SwiperSlide
                      key={index}
                      style={{
                        width: 'auto',
                        height: '100%',
                        cursor: 'pointer',
                      }}
                    >
                      <Box sx={{ position: 'relative', height: '100%' }}>
                        <Box
                          component="img"
                          src={item.type === 'video' ? item.coverUrl : item.url}
                          alt={`Thumbnail ${index + 1}`}
                          sx={{
                            width: 'auto',
                            height: '100%',
                            objectFit: 'cover',
                            borderRadius: 1,
                            opacity: 0.5,
                            transition: 'opacity 0.3s',
                            '&.swiper-slide-thumb-active': {
                              opacity: 1,
                            },
                          }}
                        />
                        {item.type === 'video' && (
                          <Box
                            sx={{
                              position: 'absolute',
                              top: '50%',
                              left: '50%',
                              transform: 'translate(-50%, -50%)',
                              color: 'white',
                              pointerEvents: 'none',
                            }}
                            onClick={handleVideoClick}
                          >
                            <PlayArrowIcon sx={{ fontSize: 20 }} />
                          </Box>
                        )}
                      </Box>
                    </SwiperSlide>
                  ))}
                </Swiper>
              </Box>
            )}
          </Box>
        </Box>
      </Dialog>

      {/* Minimal custom styles for fullscreen Swiper */}
      <style>
        {`
          .fullscreen-swiper {
            width: 100% !important;
            height: 100% !important;
          }

          .fullscreen-swiper .swiper-slide {
            width: 100%;
            height: 100%;
          }

          .fullscreen-swiper .swiper-button-next,
          .fullscreen-swiper .swiper-button-prev {
            color: white;
          }

          .fullscreen-swiper .swiper-pagination-fraction {
            color: white;
            font-size: 16px;
          }

          /* Ensure zoom container maintains aspect ratio */
          .fullscreen-swiper .swiper-zoom-container {
            width: 100%;
            height: 100%;
          }

          /* Thumbs Swiper styles */
          .thumbs-swiper-container .swiper-slide {
            opacity: 0.5;
            transition: opacity 0.3s;
          }

          .thumbs-swiper-container .swiper-slide-thumb-active {
            opacity: 1;
          }
        `}
      </style>
    </>
  )
}

export default FullscreenMediaViewer
